import React, { useState, useEffect } from 'react'
import { NotesService } from '../../services/notes.service'
import { AircraftNote } from '../../types/database'

interface NoteModalProps {
  isOpen: boolean
  onClose: () => void
  aircraftId: number
  note?: AircraftNote | null // If provided, we're editing; otherwise creating
  onSave: (savedNote: AircraftNote) => void
}

/**
 * Convert a Date to a local datetime-local input value (YYYY-MM-DDTHH:MM)
 */
function toLocalDatetimeString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

export default function NoteModal({ isOpen, onClose, aircraftId, note, onSave }: NoteModalProps) {
  const [content, setContent] = useState('')
  const [timestamp, setTimestamp] = useState('')
  const [useCustomTimestamp, setUseCustomTimestamp] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isEditing = !!note

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setContent(note?.content || '')
      setError(null)
      setSaving(false)

      if (isEditing && note) {
        // When editing, show the existing timestamp
        setTimestamp(toLocalDatetimeString(new Date(note.created_at)))
        setUseCustomTimestamp(false)
      } else {
        // When creating, default to now
        setTimestamp(toLocalDatetimeString(new Date()))
        setUseCustomTimestamp(false)
      }
    }
  }, [isOpen, note, isEditing])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!content.trim()) {
      setError('Note content is required')
      return
    }

    try {
      setSaving(true)
      setError(null)

      // Build the timestamp to send - convert local datetime to ISO string
      const customTimestamp = useCustomTimestamp && timestamp
        ? new Date(timestamp).toISOString()
        : undefined

      let savedNote: AircraftNote

      if (isEditing && note) {
        // Update existing note
        savedNote = await NotesService.update(note.id, {
          content: content.trim(),
          created_at: customTimestamp
        })
      } else {
        // Create new note
        savedNote = await NotesService.create({
          aircraft_id: aircraftId,
          content: content.trim(),
          created_at: customTimestamp
        })
      }

      onSave(savedNote)
    } catch (err: any) {
      setError(err.message || 'Failed to save note')
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    if (!saving) {
      onClose()
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 animate-fadeIn">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white transform transition-all duration-300 ease-out animate-slideIn">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Edit Note' : 'New Note'}
            </h3>
            <button
              onClick={handleClose}
              disabled={saving}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                Note Content
              </label>
              <textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter your note here..."
                disabled={saving}
                required
              />
              <div className="mt-1 text-sm text-gray-500">
                {content.length} characters
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="timestamp" className="block text-sm font-medium text-gray-700">
                  Timestamp
                </label>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useCustomTimestamp}
                    onChange={(e) => {
                      setUseCustomTimestamp(e.target.checked)
                      if (!e.target.checked) {
                        // Reset to current time (for new) or original time (for edit)
                        if (isEditing && note) {
                          setTimestamp(toLocalDatetimeString(new Date(note.created_at)))
                        } else {
                          setTimestamp(toLocalDatetimeString(new Date()))
                        }
                      }
                    }}
                    className="sr-only peer"
                    disabled={saving}
                  />
                  <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                  <span className="ms-2 text-sm text-gray-500">Custom</span>
                </label>
              </div>
              {useCustomTimestamp ? (
                <input
                  type="datetime-local"
                  id="timestamp"
                  value={timestamp}
                  onChange={(e) => setTimestamp(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  disabled={saving}
                />
              ) : (
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600">
                  {isEditing && note
                    ? new Date(note.created_at).toLocaleString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: '2-digit',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })
                    : 'Now (current time)'
                  }
                </div>
              )}
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="text-sm text-red-600">{error}</div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !content.trim()}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {saving ? 'Saving...' : (isEditing ? 'Update Note' : 'Create Note')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
